import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  Upload,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  cancelForm2181Envio,
  getForm2181EnvioSession,
  startForm2181Envio,
  submitForm2181EnvioStep,
  type Form2181EnvioSession,
} from "@/services/fiscalBenefitAdminService";

const POLL_MS = 3000;

/**
 * Drives one Form 2181 envío to Servicios en línea (spec 858fc66b).
 *
 * Deliberately NOT a fourth variant of dgi-consulta-flow-modal: that component
 * gates customer onboarding, and its `variant !== "consulta"` branches would
 * silently route this flow down the portal-user close-and-handoff path. This is
 * a root-only monthly task, so the risk of editing the signup path outweighs
 * the duplicated IDP markup below. It reuses the same SERVER session
 * machinery — only the shell is local.
 *
 * IMPORTANT: the terminal state here is "enviado", never "aceptado". DGI
 * acknowledges receipt on the page and emails the real accept/reject verdict to
 * dgi@fiscalo.uy. Nothing in this component may claim a filing succeeded.
 */
export function Form2181EnvioPanel({
  period,
  periodLabel,
  startSignal,
  busy,
  onRequestStart,
  onSubmitted,
  onDownloadPresentacion,
}: {
  period: string;
  periodLabel: string;
  /** Incremented by the page once otorgar lands, to begin the upload. */
  startSignal: number;
  /** The chain is simulating or otorgando — the button must not be re-armed. */
  busy: boolean;
  onRequestStart: () => void;
  /**
   * Fired ONCE, when DGI has taken the declaration. `fromProjection` says the
   * file was built from a dry run, so the comprobantes still have to be emitted
   * — that emission is the caller's job and must not happen any earlier.
   */
  onSubmitted: (fromProjection: boolean) => void;
  /** Manual fallback offered when the automation fails. */
  onDownloadPresentacion: () => void;
}): JSX.Element {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [session, setSession] = useState<Form2181EnvioSession | null>(null);
  const [starting, setStarting] = useState(false);
  const [documentNumber, setDocumentNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittedIdpRef = useRef(false);
  /* Emission is irreversible, so it must fire exactly once per session even
     though the poll keeps returning "submitted". */
  const submittedHandledRef = useRef(false);
  const fromProjectionRef = useRef(false);

  // Changing the period must drop the session: the panel names a period, and a
  // session left over from another month would report progress for a filing the
  // operator is no longer looking at.
  useEffect(() => {
    setSessionId(null);
    setSession(null);
    submittedIdpRef.current = false;
    submittedHandledRef.current = false;
  }, [period]);

  // The page drives the start: it owns the irreversible otorgar step, and only
  // signals here once the ledger actually holds the credits we are about to
  // declare. startSignal is a counter, not a boolean, so re-filing after a
  // failed upload opens a fresh session.
  useEffect(() => {
    if (startSignal > 0) void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSignal]);

  const status = session?.status;
  const terminal =
    status === "submitted" || status === "failed" || status === "cancelled";

  useEffect(() => {
    if (status !== "submitted" || submittedHandledRef.current) return;
    submittedHandledRef.current = true;
    onSubmitted(fromProjectionRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (!sessionId || terminal) return;
    let stop = false;
    const tick = async (): Promise<void> => {
      try {
        const s = await getForm2181EnvioSession(sessionId);
        if (!stop) setSession(s);
      } catch {
        /* transient — the next tick retries */
      }
    };
    void tick();
    const id = setInterval(() => void tick(), POLL_MS);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [sessionId, terminal]);

  const start = async (): Promise<void> => {
    setStarting(true);
    try {
      const r = await startForm2181Envio(period);
      fromProjectionRef.current = r.fromProjection;
      submittedHandledRef.current = false;
      setSessionId(r.sessionId);
      setSession({ status: "starting" });
      toast.success(
        `Enviando ${r.filename} (${r.rows} línea(s), CRC ${r.crc}). Iniciá sesión en DGI para continuar.`,
        { duration: 10000 },
      );
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data
          ?.message ?? "No pudimos iniciar el envío.";
      toast.error(msg, { duration: 8000 });
    } finally {
      setStarting(false);
    }
  };

  const chooseIdp = async (choice: "abitab" | "tuid"): Promise<void> => {
    if (!sessionId) return;
    if (choice === "abitab" && documentNumber.trim().length === 0) {
      toast.error("Ingresá el número de cédula.");
      return;
    }
    setSubmitting(true);
    try {
      await submitForm2181EnvioStep({
        sessionId,
        idpChoice: choice,
        ...(choice === "abitab"
          ? {
              abitabDocumentType: "cedula",
              abitabDocumentNumber: documentNumber.trim(),
            }
          : { tuidAppMobileConfirmed: true }),
      });
      submittedIdpRef.current = true;
    } catch {
      toast.error("No pudimos enviar la elección de identidad.");
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (): Promise<void> => {
    if (!sessionId) return;
    try {
      await cancelForm2181Envio(sessionId);
    } catch {
      /* the poll will show whatever actually happened */
    }
    setSessionId(null);
    setSession(null);
  };

  if (!sessionId) {
    return (
      <Button type="button" onClick={onRequestStart} disabled={busy || starting}>
        {busy || starting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <Upload className="mr-2 h-4 w-4" aria-hidden />
        )}
        Subir a DGI
      </Button>
    );
  }

  const awaitingIdp =
    status === "awaiting_idp" && !submittedIdpRef.current;

  return (
    <div className="w-full rounded-md border bg-muted/30 p-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="font-medium">Envío de {periodLabel} a DGI</p>
          {status === "submitted" ? (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              {/* "Enviado", not "aceptado" — see the file header. */}
              {session?.dgiNumero
                ? `Recibido por DGI — número ${session.dgiNumero}. `
                : "Presentado. "}
              {fromProjectionRef.current
                ? "Emitiendo los comprobantes… El resultado de la declaración llega por correo a dgi@fiscalo.uy."
                : "El resultado llega por correo a dgi@fiscalo.uy."}
            </p>
          ) : status === "failed" || status === "cancelled" ? (
            <p className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              {session?.lastError?.trim() || "El envío no se completó."}
            </p>
          ) : awaitingIdp ? (
            <p className="text-xs text-muted-foreground">
              Elegí cómo iniciar sesión en ID Uruguay.
            </p>
          ) : (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {status === "starting"
                ? "Abriendo la sesión…"
                : "Trabajando en DGI…"}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void cancel()}
        >
          <X className="h-4 w-4" aria-hidden />
          <span className="sr-only">Cerrar</span>
        </Button>
      </div>

      {awaitingIdp ? (
        <div className="mt-3 space-y-2">
          <div className="space-y-1">
            <Label htmlFor="envio-ci" className="text-xs">
              Cédula (para Abitab)
            </Label>
            <Input
              id="envio-ci"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
              placeholder="12345678"
              className="h-8 w-48"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => void chooseIdp("abitab")}
              disabled={submitting}
            >
              Abitab
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void chooseIdp("tuid")}
              disabled={submitting}
            >
              TuID
            </Button>
          </div>
        </div>
      ) : null}

      {status === "failed" || status === "cancelled" ? (
        <div className="mt-3">
          {/* The only remaining way to file by hand: the standalone
              presentation download was removed from the action row, so a failed
              automation must not leave the operator with no path to DGI. */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onDownloadPresentacion}
          >
            <Download className="mr-2 h-3.5 w-3.5" aria-hidden />
            Descargar el archivo y subirlo a mano
          </Button>
        </div>
      ) : null}

      {/* DGI's own words, shown verbatim. Until a real success string is known
          the flow cannot confirm the outcome, so the operator reads this. */}
      {status === "submitted" && session?.rawMessage ? (
        <pre className="mt-3 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-background p-2 text-[11px] text-muted-foreground">
          {session.rawMessage}
        </pre>
      ) : null}
    </div>
  );
}
